/**
 * LoanRowCard — type + status chips used inside the Loans list table cells.
 * Maps spec tones to design-system colors:
 *   - Bridge / hard-money → orange
 *   - DSCR / rental → purple (id)
 *   - Construction → blue
 *   - Commercial → mint
 *
 * Status:
 *   - active / funded → mint (success)
 *   - awaiting-you / in-progress → amber (warning)
 *   - submitted / under-review → blue (primary)
 *   - closed / withdrawn / rejected → default
 */

'use client';

import { GlassChip, type SxProps, type Theme } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';

type ChipColor = 'success' | 'warning' | 'primary' | 'secondary' | 'info' | 'error' | 'default';

const LOAN_TYPE_COLORS: Record<string, ChipColor> = {
  bridge: 'warning',
  hard_money: 'warning',
  hardmoney: 'warning',
  dscr: 'secondary',
  rental: 'secondary',
  construction: 'info',
  commercial: 'success',
  general: 'default',
};

export function loanTypeColor(type: string | undefined | null): ChipColor {
  if (!type) return 'default';
  const key = type.toLowerCase().replace(/[\s-]+/g, '_');
  return LOAN_TYPE_COLORS[key] ?? 'default';
}

export function loanStatusColor(status: string | undefined | null): ChipColor {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'funded':
    case 'active':
    case 'converted':
    case 'completed':
      return 'success';
    case 'in_progress':
    case 'draft':
      return 'warning';
    case 'submitted':
    case 'new':
    case 'pending_review':
    case 'under_review':
    case 'pending':
    case 'reviewing':
      return 'primary';
    case 'rejected':
      return 'error';
    case 'closed':
    case 'withdrawn':
      return 'default';
    default:
      return 'default';
  }
}

export interface LoanTypeChipProps {
  label: string;
  type: string | null | undefined;
  sx?: SxProps<Theme>;
}

export function LoanTypeChip({ label, type, sx }: LoanTypeChipProps): ReactElement {
  return <GlassChip size="small" label={label} color={loanTypeColor(type)} sx={sx} />;
}

export interface LoanStatusChipProps {
  label: string;
  status: string | null | undefined;
  sx?: SxProps<Theme>;
}

export function LoanStatusChip({ label, status, sx }: LoanStatusChipProps): ReactElement {
  return <GlassChip size="small" label={label} color={loanStatusColor(status)} sx={sx} />;
}
